import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import styled from "styled-components";
import { Textarea } from "../../../form/textarea";
import { updateNodeValue } from "../../../../services/method";
import { operationType, selectDataType } from "../../../../services/types";
import { TextInput } from "../../../form/textInput";
import { Select } from "../../../form/select";
import { AuthenticateSelect } from "../../../auth/select";
import { SettingsBlock, settingsDataType } from "../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../expanded";

type BigQueryProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function BigQuery({ id, data, setChildMinimize, setChildSettings }: BigQueryProps) {
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'Run Query', refId: 'run_query', isActive: true, }, { id: 2, name: 'List Datasets', refId: 'list_dataset' }, 
      { id: 3, name: 'Create Dataset', refId: 'create_dataset' }, { id: 4, name: 'Update Dataset', refId: 'update_dataset' }, 
      { id: 5, name: 'Delete Dataset', refId: 'delete_dataset' }, { id: 6, name: 'List Tables', refId: 'list_tables' }, 
      { id: 7, name: 'Create Table', refId: 'create_table' }, { id: 8, name: 'Update Table', refId: 'update_table' },
      { id: 9, name: 'Delete Table', refId: 'delete_table' }, { id: 10, name: 'Let AI Agent Decide', refId: 'agent_decide' }
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { request: requests[0].refId });
      } else {
         setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === data.value.request })));
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleAuthenticate = async (type: 'add' | 'remove', data: any) => {
      if (type === 'add') { 
         // scope="https://www.googleapis.com/auth/bigquery https://www.googleapis.com/auth/cloud-platform https://www.googleapis.com/auth/cloud-platform.read-only"
         updateNodeValue(id, setNodes, { connection: data });
      } else if (type === 'remove') {
         updateNodeValue(id, setNodes, { connection: null });
      }
   }

   // ---
   const handleSelectRequests = (selectId: number) => {
      setRequests((requests) => requests.map((request) => {
         if (request.id === selectId) return { ...request, isActive: true };
         else return { ...request, isActive: false };
      }));
      updateNodeValue(id, setNodes, { request: requests.find(obj => obj.id === selectId)?.refId });
   }

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   }

   // ---
   const handleChangeAny = (e: React.ChangeEvent<HTMLInputElement>) => { updateNodeValue(id, setNodes, { any: e.target.value }) };
   const handleChangeSchema = (e: React.ChangeEvent<HTMLInputElement>) => { updateNodeValue(id, setNodes, { schema: e.target.value }) };
   const isWarning = !data.value?.connection || !data.value?.project_id;
   
   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            // styleParent={ (data.value?.files?.length ? { right: '-101.5px' } : {}) }
            style={{ minWidth: '420px' }}
         ><Styled>
            <AuthenticateSelect type="google" defaultId={ data.value?.connection?.id } handleAuthenticate={ handleAuthenticate } withLine={ true } background={ data.background } />
            <Select title="Request" data={ requests } handleSelectItem={ handleSelectRequests } isSmall={ true } />
            <TextInput keyRef="project_id" title="Project Id" placeholder="project_id" value={ data.value?.project_id } handleChange={ handleChangeInput } />
            { ['create_dataset', 'update_dataset', 'delete_dataset', 'list_tables', 'create_table', 'update_table', 'delete_table'].includes(data.value?.request) ? 
               <TextInput keyRef="dataset_id" title="Dataset Id" placeholder="dataset_id" value={ data.value?.dataset_id } handleChange={ handleChangeInput } />
            : null }
            { ['create_dataset', 'update_dataset'].includes(data.value?.request) ? 
               <TextInput keyRef="friendly_name" title="Friendly" placeholder="friendly_name" value={ data.value?.friendly_name } handleChange={ handleChangeInput } />
            : null }
            { ['create_table', 'update_table', 'delete_table'].includes(data.value?.request) ? 
               <TextInput keyRef="table_id" title="Table Id" placeholder="table_id" value={ data.value?.table_id } handleChange={ handleChangeInput } />
            : null }
            { ['create_table', 'update_table'].includes(data.value?.request) ? 
               <Textarea keyRef="schema" value={ data.value?.schema } placeholder="schema" handleChange={ handleChangeSchema } /> 
            : null }
            { ['run_query', 'create_dataset', 'update_dataset', 'create_table', 'update_table'].includes(data.value?.request) ? 
               <Textarea value={ data.value?.any } placeholder={ data.value?.request === 'run_query' ? 'input_query' : 'description'} handleChange={ handleChangeAny } /> 
            : null }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
const Main = styled.div`
   min-width: 150px;
   padding: 0;
`;

const Styled = styled.div`
   .textInput{
      h4 {
         width: 105px;
      }
      &:not(:last-of-type) {
         margin-bottom: 10px;
      }
   }
   .select {
      margin-bottom: 10px;
      h4 { width: 85px; }
   }
   .schema {
      margin-bottom: 7px;
   }
   .textarea {
      &:last-of-type {
         margin-top: 10px;
      }
      > div {
         height: 80px;
         min-height: 80px;
      }
   }
`;